import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Quote, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Reveal } from "./Reveal";
import { TESTIMONIALS } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function initials(name) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const inputBase =
  "w-full bg-transparent border-b border-[#2C3C30]/25 focus:border-[#2C3C30] outline-none py-2.5 font-sans text-[#1A211D] placeholder:text-[#7D8C7A] transition-colors duration-300";

function ShareForm() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    location: "",
    concern: "",
    quote: "",
  });
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.quote.trim().length < 10) {
      toast.error("Please add your name and a review of at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, {
        ...form,
        rating,
      });
      toast.success("Thank you! Your review was submitted and will appear once approved.");
      setForm({ name: "", location: "", concern: "", quote: "" });
      setRating(5);
      setOpen(false);
    } catch (err) {
      toast.error("Could not submit your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="open-testimonial-form"
          className="group inline-flex items-center gap-2 rounded-full bg-[#2C3C30] px-6 py-3 text-[#F7F6F2] font-medium text-sm transition-colors duration-300 hover:bg-[#1A211D]"
        >
          <Plus size={16} /> Share your experience
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#F7F6F2] border-[#2C3C30]/15 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#2C3C30] tracking-tight text-left">
            Share your experience
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} data-testid="testimonial-form" className="space-y-5 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">
                Your name
              </label>
              <input
                className={inputBase}
                value={form.name}
                onChange={update("name")}
                placeholder="e.g. Ananya Iyer"
                data-testid="testimonial-name"
              />
            </div>
            <div>
              <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">
                City (optional)
              </label>
              <input
                className={inputBase}
                value={form.location}
                onChange={update("location")}
                placeholder="e.g. Mumbai"
                data-testid="testimonial-location"
              />
            </div>
          </div>
          <div>
            <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">
              What did we help with? (optional)
            </label>
            <input
              className={inputBase}
              value={form.concern}
              onChange={update("concern")}
              placeholder="e.g. IBS & Bloating"
              data-testid="testimonial-concern"
            />
          </div>
          <div>
            <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158] block mb-2">
              Your rating
            </label>
            <div className="flex gap-1.5" data-testid="testimonial-rating">
              {Array.from({ length: 5 }).map((_, i) => {
                const val = i + 1;
                const active = (hoverRating || rating) >= val;
                return (
                  <button
                    type="button"
                    key={val}
                    onClick={() => setRating(val)}
                    onMouseEnter={() => setHoverRating(val)}
                    onMouseLeave={() => setHoverRating(0)}
                    data-testid={`star-${val}`}
                    aria-label={`${val} star${val > 1 ? "s" : ""}`}
                  >
                    <Star
                      size={26}
                      className={
                        active
                          ? "fill-[#2C3C30] text-[#2C3C30]"
                          : "text-[#7D8C7A]"
                      }
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#566158]">
              Your review
            </label>
            <textarea
              rows={4}
              className={`${inputBase} resize-none`}
              value={form.quote}
              onChange={update("quote")}
              placeholder="Tell others about your journey..."
              data-testid="testimonial-quote"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-testimonial-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#2C3C30] px-8 py-3 text-[#F7F6F2] font-medium transition-colors duration-300 hover:bg-[#1A211D] disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Submitting...
              </>
            ) : (
              "Submit review"
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Testimonials() {
  const [dynamic, setDynamic] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then(({ data }) => setDynamic(data || []))
      .catch(() => setDynamic([]));
  }, []);

  const all = [...dynamic, ...TESTIMONIALS];

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40"
      data-testid="testimonials-section"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 md:mb-20 gap-6">
        <div>
          <Reveal>
            <p className="font-sans text-xs tracking-[0.4em] uppercase text-[#7D8C7A] mb-6">
              Stories of healing
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tight text-[#2C3C30]">
              What clients say
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="flex flex-col items-start gap-5 md:items-end">
            <p className="font-sans text-[#566158] max-w-sm md:text-right">
              Real people, real gut-health journeys — a few words from those who
              walked this path with us.
            </p>
            <ShareForm />
          </div>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {all.map((t, i) => (
          <Reveal key={t.id || t.name + i} delay={(i % 3) * 0.1}>
            <figure
              className="group relative h-full flex flex-col bg-[#EAE7E0] rounded-3xl p-8 border border-[#2C3C30]/10 transition-transform duration-500 hover:-translate-y-2"
              data-testid={`testimonial-card-${i}`}
            >
              <Quote size={34} className="text-[#7D8C7A] mb-5 shrink-0" aria-hidden="true" />
              <div className="flex gap-1 mb-5" aria-label={`${t.rating || 5} out of 5 stars`}>
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star
                    key={s}
                    size={16}
                    className={
                      s < (t.rating || 5)
                        ? "fill-[#2C3C30] text-[#2C3C30]"
                        : "text-[#7D8C7A]/40"
                    }
                  />
                ))}
              </div>
              <blockquote className="font-sans text-[#1A211D] leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="flex items-center gap-4 mt-8 pt-6 border-t border-[#2C3C30]/15">
                <span className="flex items-center justify-center w-12 h-12 rounded-full bg-[#2C3C30] text-[#F7F6F2] font-serif text-lg shrink-0">
                  {initials(t.name)}
                </span>
                <span>
                  <span className="block font-sans font-semibold text-[#2C3C30]">
                    {t.name}
                  </span>
                  <span className="block font-sans text-sm text-[#566158]">
                    {[t.location, t.concern].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
